import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import Switch from "@mui/material/Switch";
import lockedIcon from "images/Locked_icon.png";
import unlockedIcon from "images/Unlocked_icon.png";
import DoneRoundedIcon from "@mui/icons-material/DoneRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import LockOpenRoundedIcon from "@mui/icons-material/LockOpenRounded";
import dayjs from "dayjs";
import { Button } from '../../../Component/CommonFormEle';
import { fetchDeviceSettings, fetchPolicyGroupSettings, updateDeviceSetting, updatePolicyGroupSetting, updatePolicySettingLock } from "js/Api/index";

function DoNotDisturb({ user, policy, settingsFrom }) {
    const [settings, setSettings] = useState([]);
    const [selectedDNDOption, setSelectedDNDOption] = useState("allow");
    const [activeCallsOptionButton, setActiveCallsOptionButton] = useState(1);
    const daysOfWeek = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const [isDndEnabled, setIsDndEnabled] = useState(false);

    const [startHour, setStartHour] = useState("12");
    const [startMinute, setStartMinute] = useState("00");
    const [startPeriod, setStartPeriod] = useState("AM");
    const [endHour, setEndHour] = useState("12");
    const [endMinute, setEndMinute] = useState("00");
    const [endPeriod, setEndPeriod] = useState("PM");
    const [policySettingCategoryGroups, setPolicySettingCategoryGroups] =
        useState([]);

    const isReadOnly = selectedDNDOption !== "manage";

    const hours = Array.from({ length: 13 }, (_, i) =>
        i.toString().padStart(2, "0")
    );
    const minutes = Array.from({ length: 60 }, (_, i) =>
        i.toString().padStart(2, "0")
    );
    const periods = ["AM", "PM"];

    useEffect(() => {
        if (settingsFrom === "ManageDevices") {
            fetchDND();
        } else if (settingsFrom === "PolicyGroups") {
            fetchPolicyGroupDND();
        }
    }, []);

    const fetchDND = async () => {
        try {
            const response = await fetchDeviceSettings(user.id);

            if (response.success) {
                setSettings(response.data);
                const doNotDisturbSetting = response.data.find(
                    (setting) => setting.name === "Do Not Disturb"
                );
                if (doNotDisturbSetting) {
                    updateDNDState(doNotDisturbSetting);
                }
            } else {
                console.error("Failed to fetch Do Not Disturb settings:", response.error);
            }
        } catch (error) {
            console.error("Error fetching Do Not Disturb settings:", error);
        }
    };

    const fetchPolicyGroupDND = async () => {
        try {
            const response = await fetchPolicyGroupSettings(policy.id);

            if (response.success) {
                setSettings(response.data.polictSettings);
                setPolicySettingCategoryGroups(response.data.policySettingCategoryGroups);

                const doNotDisturbSetting = response.data.polictSettings.find((setting) => setting.friendlyName === "settings.dnd");
                if (doNotDisturbSetting) {
                    updateDNDState(doNotDisturbSetting);
                }

                updateSelectedDNDOption(response.data.policySettingCategoryGroups);
            } else {
                console.error("Failed to fetch Do Not Disturb settings:", response.error);
            }
        } catch (error) {
            console.error("Error fetching Do Not Disturb settings:", error);
        }
    };

    const updateSelectedDNDOption = (categoryGroups) => {
        const dndCategory = categoryGroups.find(
            (category) => category.categoryName === "Do Not Disturb"
        );

        if (dndCategory) {
            setSelectedDNDOption(dndCategory.locked ? "manage" : "allow");
        }
    };

    const handleCategoryLockChange = async (locked) => {
        const category = policySettingCategoryGroups.find(
            (category) => category.categoryName === "Do Not Disturb"
        );

        if (!category) return;

        try {
            const response = await updatePolicySettingLock(policy.id, category.id, locked);

            if (response.success) {
                // Update local state
                category.locked = locked;
                setPolicySettingCategoryGroups([...policySettingCategoryGroups]);
                setSelectedDNDOption(locked ? "manage" : "allow");
            } else {
                console.error("Failed to update category lock:", response.error);
                toast.error("Failed to update category lock");
            }
        } catch (error) {
            console.error("Error updating category lock:", error);
            toast.error("Failed to update category lock");
        }
    };

    const updateDNDState = (doNotDisturbSetting) => {
        let parsedValue = doNotDisturbSetting.value
            ? JSON.parse(doNotDisturbSetting.value)
            : null;

        if (parsedValue === null) {
            const defaultStartTime = new Date();
            defaultStartTime.setHours(0, 0, 0, 0);

            const defaultEndTime = new Date();
            defaultEndTime.setHours(12, 0, 0, 0);

            parsedValue = {
                start: defaultStartTime.toISOString(),
                end: defaultEndTime.toISOString(),
                selectedDay: [],
                isDndEnabled: false,
                CallHandling: "BlockCalls",
            };
        }

        const { start, end, selectedDay, isDndEnabled, CallHandling } = parsedValue;

        setIsDndEnabled(isDndEnabled || false);
        const startDateTime = dayjs(start);
        const endDateTime = dayjs(end);

        setStartHour(startDateTime.format("hh"));
        const parsedStartMinute = parseInt(startDateTime.format("mm"), 10);
        setStartMinute(parsedStartMinute);
        setStartPeriod(startDateTime.format("A"));

        setEndHour(endDateTime.format("hh"));
        const parsedEndMinute = parseInt(endDateTime.format("mm"), 10);
        setEndMinute(parsedEndMinute);
        setEndPeriod(endDateTime.format("A"));

        setStartMinute(parsedStartMinute.toString().padStart(2, "0"));
        setEndMinute(parsedEndMinute.toString().padStart(2, "0"));

        const updatedCheckedDays = {};
        daysOfWeek.forEach((day, index) => {
            updatedCheckedDays[day] = selectedDay.includes(index + 1);
        });
        setCheckedDays(updatedCheckedDays);

        setActiveCallsOptionButton(CallHandling === "AllowCalls" ? 2 : 1);
    };

    const handleSaveDND = async () => {
        try {

            const startDate = new Date();
            let hours24 = parseInt(startHour);

            if (startPeriod === "AM") {
                if (parseInt(startHour) === 12) hours24 = 0;
            } else {
                if (parseInt(startHour) !== 12) hours24 += 12;
            }

            startDate.setHours(hours24, startMinute, 0, 0);

            const endDate = new Date();
            hours24 = parseInt(endHour);
            if (endPeriod === "AM") {
                if (parseInt(endHour) === 12) hours24 = 0;
            } else {
                if (parseInt(endHour) !== 12) hours24 += 12;
            }

            endDate.setHours(hours24, endMinute, 0, 0);


            //const startDate = new Date();
            //startDate.setHours(
            //    startPeriod === "AM" ? parseInt(startHour) : parseInt(startHour) + 12
            //);
            //const startMinuteValue = parseInt(startMinute, 10);
            //startDate.setMinutes(parseInt(startMinuteValue));

            
            //endDate.setHours(
            //    endPeriod === "AM" ? parseInt(endHour) : parseInt(endHour) + 12
            //);
            //const endMinuteValue = parseInt(endMinute, 10);
            //endDate.setMinutes(endMinuteValue);

            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                throw new Error("Invalid start or end time");
            }

            const selectedDayNumbers = Object.keys(checkedDays)
                .filter((day) => checkedDays[day])
                .map((day) => daysOfWeek.indexOf(day) + 1);

            const CallHandling =
                activeCallsOptionButton === 2 ? "AllowCalls" : "BlockCalls";

            let value;
            if (settingsFrom === "ManageDevices") {
                value = {
                    start: startDate.toISOString(),
                    end: endDate.toISOString(),
                    selectedDay: selectedDayNumbers,
                    isDndEnabled: isDndEnabled,
                    CallHandling: CallHandling,
                };
            } else if (settingsFrom === "PolicyGroups") {
                value = {
                    start: startDate.toISOString(),
                    end: endDate.toISOString(),
                    selectedDay: selectedDayNumbers,
                    isDndEnabled: isDndEnabled,
                    CallHandling: CallHandling,
                };
            }

            const updatedSettings = settings
                .map((setting) => {
                    if (
                        setting.name === "Enable Do Not Disturb" ||
                        setting.name === "Do Not Disturb"
                    ) {
                        return {
                            ...setting,
                            value: JSON.stringify(value),
                        };
                    }
                    return setting;
                })
                .filter(
                    (setting) =>
                        setting.name === "Enable Do Not Disturb" ||
                        setting.name === "Do Not Disturb"
                );

            let hasError = false;

            if (settingsFrom === "ManageDevices") {
                // Update device settings
                for (const setting of updatedSettings) {
                    const response = await updateDeviceSetting(setting);

                    if (!response.success) {
                        hasError = true;
                        console.error("Failed to update device setting:", response.error);
                        break;
                    }
                }
            } else if (settingsFrom === "PolicyGroups") {
                // Update policy group settings
                for (const setting of updatedSettings) {
                    const response = await updatePolicyGroupSetting(policy.id, setting.id, setting.value);

                    if (!response.success) {
                        hasError = true;
                        console.error("Failed to update policy setting:", response.error);
                        break;
                    }
                }
            }

            if (!hasError) {
                toast.success("Do Not Disturb settings updated successfully!");

                // Refresh the settings
                if (settingsFrom === "ManageDevices") {
                    fetchDND();
                } else {
                    fetchPolicyGroupDND();
                }
            } else {
                toast.error("Failed to update Do Not Disturb settings");
            }
        } catch (error) {
            console.error("Error updating DND settings:", error);
            toast.error("Failed to update Do Not Disturb settings");
        }
    };

    const handleStartHourChange = (event) => setStartHour(event.target.value);
    const handleStartMinuteChange = (event) => setStartMinute(event.target.value);
    const handleStartPeriodChange = (event) => setStartPeriod(event.target.value);

    const handleEndHourChange = (event) => setEndHour(event.target.value);
    const handleEndMinuteChange = (event) => setEndMinute(event.target.value);
    const handleEndPeriodChange = (event) => setEndPeriod(event.target.value);

    const [checkedDays, setCheckedDays] = useState({
        Monday: false,
        Tuesday: false,
        Wednesday: false,
        Thursday: false,
        Friday: false,
        Saturday: false,
        Sunday: false,
    });

    const handleCheckboxChange = (event) => {
        const { id, checked } = event.target;
        setCheckedDays((prevCheckedDays) => ({
            ...prevCheckedDays,
            [id]: checked,
        }));
    };

    const handleCallsOptionButtonClick = (buttonNumber) => {
        if (activeCallsOptionButton === buttonNumber) {
            setActiveCallsOptionButton(null);
        } else {
            setActiveCallsOptionButton(buttonNumber);
        }
    };

    return (
        <>
            <div className="row">
                <div className="col-md-12">
                    <h4 className="d-inline">Do Not Disturb</h4>
                    <div className="dropdown d-inline">
                        <button
                            className="btn lockerBtn"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                        >
                            {selectedDNDOption === "allow" ? (
                                <img src={unlockedIcon} style={{ height: "20px" }} />
                            ) : (
                                <img src={lockedIcon} style={{ height: "20px" }} />
                            )}
                        </button>
                        <ul className="dropdown-menu dropdown-item-ul ">
                            <li onClick={() => handleCategoryLockChange(false)}>
                                <span>
                                    <LockOpenRoundedIcon
                                        sx={{
                                            width: "20px",
                                            margin: "0 5px",
                                        }}
                                    />
                                </span>
                                Allow device user and family to change these settings
                                {selectedDNDOption === "allow" && (
                                    <span style={{ float: "right", marginRight: "1em" }}>
                                        <DoneRoundedIcon />
                                    </span>
                                )}
                            </li>
                            <li onClick={() => handleCategoryLockChange(true)}>
                                <span>
                                    <LockRoundedIcon
                                        sx={{
                                            width: "20px",
                                            margin: "0px 5px",
                                        }}
                                    />
                                </span>
                                Organisation manages these settings
                                {selectedDNDOption === "manage" && (
                                    <span style={{ float: "right", marginRight: "1em" }}>
                                        <DoneRoundedIcon />
                                    </span>
                                )}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            <div className="row mt-2">
                <div className="col-md-3">
                    Enable Do Not Disturb
                </div>
                <div className="col-md-9">
                    <Switch checked={isDndEnabled} onChange={(e) => setIsDndEnabled(e.target.checked)} disabled={isReadOnly} />
                </div>
            </div>
            {isDndEnabled && (
                <>
                    <div className="row mt-2">
                        <div className="col-md-3">
                            Block or allow calls?
                        </div>
                        <div className="col-md-9">
                            <div className="form-check mr-4 ml-3 d-inline">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="flexRadioDefault"
                                    id="flexRadioDefault1"
                                    checked={activeCallsOptionButton === 1}
                                    onChange={() => handleCallsOptionButtonClick(1)}
                                    disabled={isReadOnly}
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor="flexRadioDefault1"
                                >
                                    Block calls
                                </label>
                            </div>
                            <div className="form-check d-inline">
                                <input
                                    className="form-check-input"
                                    type="radio"
                                    name="flexRadioDefault"
                                    id="flexRadioDefault2"
                                    checked={activeCallsOptionButton === 2}
                                    onChange={() => handleCallsOptionButtonClick(2)}
                                    disabled={isReadOnly}
                                />
                                <label
                                    className="form-check-label"
                                    htmlFor="flexRadioDefault2"
                                >
                                    Allow calls
                                </label>
                            </div>
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-md-3">
                            Start time
                        </div>
                        <div className="col-md-9">
                            <select style={{ width: "auto" }}
                                className="form-control d-inline"
                                value={startHour}
                                onChange={handleStartHourChange}
                                disabled={isReadOnly}
                            >
                                {hours.map((hour) => (
                                    <option key={hour} value={hour}>
                                        {hour}
                                    </option>
                                ))}
                            </select>
                            <strong>:</strong>
                            <select
                                style={{ width: "auto", marginLeft: "1rem" }}
                                className="form-control d-inline"
                                value={startMinute}
                                onChange={handleStartMinuteChange}
                                disabled={isReadOnly}
                            >
                                {minutes.map((minute) => (
                                    <option key={minute} value={minute}>
                                        {minute}
                                    </option>
                                ))}
                            </select>
                            <select
                                style={{ width: "auto", marginLeft: "1rem" }}
                                className="form-control d-inline"
                                value={startPeriod}
                                onChange={handleStartPeriodChange}
                                disabled={isReadOnly}
                            >
                                {periods.map((period) => (
                                    <option key={period} value={period}>
                                        {period}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-md-3">
                            End time
                        </div>
                        <div className="col-md-9">
                            <select
                                style={{ width: "auto" }}
                                className="form-control d-inline"
                                value={endHour}
                                onChange={handleEndHourChange}
                                disabled={isReadOnly}
                            >
                                {hours.map((hour) => (
                                    <option key={hour} value={hour}>
                                        {hour}
                                    </option>
                                ))}
                            </select>
                            <strong>:</strong>
                            <select
                                style={{ width: "auto", marginLeft: "1rem" }}
                                className="form-control d-inline"
                                value={endMinute}
                                onChange={handleEndMinuteChange}
                                disabled={isReadOnly}
                            >
                                {minutes.map((minute) => (
                                    <option key={minute} value={minute}>
                                        {minute}
                                    </option>
                                ))}
                            </select>
                            <select
                                style={{ width: "auto", marginLeft: "1rem" }}
                                className="form-control d-inline"
                                value={endPeriod}
                                onChange={handleEndPeriodChange}
                                disabled={isReadOnly}
                            >
                                {periods.map((period) => (
                                    <option key={period} value={period}>
                                        {period}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-md-3">
                            Active days
                        </div>
                        <div className="col-md-9">
                            {Object.keys(checkedDays).map((day) => (
                                <div key={day} className="form-check form-check-inline">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={day}
                                        value={day}
                                        checked={checkedDays[day]}
                                        onChange={handleCheckboxChange}
                                        disabled={isReadOnly}
                                    />
                                    <label className="form-check-label mr-3" htmlFor={day}>
                                        {day.slice(0, 3)}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            <div className="row mt-4">
                <div className="col-md-12">
                    <Button onClick={handleSaveDND} variant="black">
                        Apply
                    </Button>
                </div>
            </div>
        </>
    );
}

export default DoNotDisturb;